import React, { useState } from "react";
import "@/App.css";
import { Upload, Camera, Download, Printer, CheckCircle2, FileText, User, Phone, Mail, Briefcase, Users, LogOut, Wine, ChefHat, AlertTriangle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "./Dashboard";
import Bar from "./Bar";
import Kitchen from "./Kitchen";
import Login from "./Login";
import { AuthProvider, useAuth } from "./AuthContext";
import { BrandingProvider, useBranding } from "./BrandingContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable slot for an Aadhar side upload — provides both file-picker and camera capture
// with a live preview thumbnail. Rendered once per side (front / back / full).
function SideUploadSlot({ side, label, hint, preview, onFile, onClear, disabled }) {
  const fileInputId = `aadhar-file-${side}`;
  const camInputId = `aadhar-cam-${side}`;
  return (
    <div className="border border-slate-200 rounded-2xl p-3 bg-slate-50" data-testid={`aadhar-slot-${side}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-slate-900">{label}</p>
          <p className="text-xs text-slate-500 truncate">{hint}</p>
        </div>
        {preview && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-red-500 hover:text-red-700 font-medium ml-2 shrink-0"
            data-testid={`aadhar-slot-clear-${side}`}
          >
            Clear
          </button>
        )}
      </div>
      {preview ? (
        <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
          <img
            src={preview}
            alt={label}
            className="w-full h-32 object-contain bg-slate-50"
            data-testid={`aadhar-slot-preview-${side}`}
          />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <input
            type="file"
            accept="image/*,application/pdf,.heic,.heif"
            className="hidden"
            id={fileInputId}
            onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); e.target.value = ""; }}
            data-testid={`aadhar-slot-file-input-${side}`}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            id={camInputId}
            onChange={(e) => { if (e.target.files?.[0]) onFile(e.target.files[0]); e.target.value = ""; }}
            data-testid={`aadhar-slot-cam-input-${side}`}
          />
          <Button
            variant="outline"
            disabled={disabled}
            onClick={() => document.getElementById(fileInputId).click()}
            className="h-10 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5 font-medium text-xs"
            data-testid={`aadhar-slot-upload-${side}`}
          >
            <Upload className="w-3.5 h-3.5" />
            Upload
          </Button>
          <Button
            variant="outline"
            disabled={disabled}
            onClick={() => document.getElementById(camInputId).click()}
            className="h-10 rounded-lg border-amber-300 text-amber-700 hover:bg-amber-50 gap-1.5 font-medium text-xs"
            data-testid={`aadhar-slot-camera-${side}`}
          >
            <Camera className="w-3.5 h-3.5" />
            Camera
          </Button>
        </div>
      )}
    </div>
  );
}

// Send cookies with every request
axios.defaults.withCredentials = true;

function MainApp() {
  const { user, logout } = useAuth();
  const branding = useBranding();
  const initialView = user?.role === "bar_staff" ? "bar" : "create";
  const [view, setView] = useState(initialView); // "create" | "dashboard" | "bar" | "kitchen"
  const isAdmin = user?.role === "admin";
  const isBarStaff = user?.role === "bar_staff";
  const isMemberStaff = user?.role === "member_staff";
  const [currentStep, setCurrentStep] = useState(1);
  const [aadharData, setAadharData] = useState(null);
  const [aadharPath, setAadharPath] = useState("");
  const [aadharBackPath, setAadharBackPath] = useState("");
  const [aadharMode, setAadharMode] = useState("two_side"); // "two_side" | "full_page"
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontPreview, setFrontPreview] = useState("");
  const [backPreview, setBackPreview] = useState("");
  const [photoPath, setPhotoPath] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [memberId, setMemberId] = useState("");
  const [formPreviewUrl, setFormPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    father_name: "",
    dob: "",
    aadhar_number: "",
    age: "",
    address: "",
    mobile: "",
    email: "",
    occupation: "",
    referrer_name: "",
    referrer_number: "",
    member_number: ""
  });

  // Live duplicate check (Aadhar / Mobile) — debounced
  React.useEffect(() => {
    const aadhar = (formData.aadhar_number || "").trim();
    const mobile = (formData.mobile || "").trim();
    if (!aadhar && !mobile) { setDupWarning(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API}/check-duplicate`, { params: { aadhar, mobile } });
        setDupWarning(data.duplicate ? data : null);
      } catch { setDupWarning(null); }
    }, 600);
    return () => clearTimeout(t);
  }, [formData.aadhar_number, formData.mobile]);

  // Auto-fetch next Member ID (SH <highest+1>) on mount and after each successful create
  React.useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/next-member-number`).then((r) => {
      if (!cancelled) setFormData((f) => ({ ...f, member_number: r.data.member_number || "" }));
    }).catch(() => { /* silent — user can still submit */ });
    return () => { cancelled = true; };
  }, []);

  // Aadhar processing handler. Sends the selected files to the multi endpoint
  // (single file → treated as full-page; two files → front+back merge).
  const processAadhar = async (files) => {
    const arr = files.filter(Boolean);
    if (!arr.length) {
      toast.error("Please select at least one Aadhar image");
      return;
    }
    const [front, back] = arr;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('front', front);
      if (back) fd.append('back', back);
      toast.info(back ? "Scanning both sides + QR..." : "Scanning & translating Aadhar...");
      const response = await axios.post(`${API}/upload-aadhar-multi`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setAadharPath(response.data.storage_path);
      setAadharBackPath(response.data.back_storage_path || "");
      setAadharData(response.data.data);
      setFormData(prev => ({ ...prev, ...response.data.data }));
      const qrHit = response.data.qr_detected_front || response.data.qr_detected_back;
      toast.success(qrHit ? "QR decoded — all details extracted!" : "Aadhar scanned & translated to Tamil!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to scan Aadhar. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Assign a selected/captured file to front or back slot and show a local preview.
  const selectSide = (side, file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (side === "front") {
      setFrontFile(file);
      setFrontPreview(url);
    } else {
      setBackFile(file);
      setBackPreview(url);
    }
  };

  const clearAadharSelection = () => {
    setFrontFile(null); setBackFile(null);
    setFrontPreview(""); setBackPreview("");
    setAadharData(null); setAadharPath(""); setAadharBackPath("");
  };

  // Reusable Photo upload handler
  const uploadPhotoFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      toast.info("Uploading photo...");
      const response = await axios.post(`${API}/upload-photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPhotoPath(response.data.storage_path);
      setPhotoPreview(`${API}/file/${response.data.storage_path}?t=${Date.now()}`);
      toast.success("Photo uploaded!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to upload photo.");
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps: getPhotoRootProps, getInputProps: getPhotoInputProps, isDragActive: isPhotoDragActive } = useDropzone({
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.webp', '.heic', '.heif'],
      'application/pdf': ['.pdf']
    },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles.length > 0) uploadPhotoFile(acceptedFiles[0]);
    }
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNextStep = async () => {
    if (currentStep === 1 && !aadharData) {
      toast.error("Please upload Aadhar card first");
      return;
    }
    if (currentStep === 2 && !photoPath) {
      toast.error("Please upload a photo first");
      return;
    }
    if (currentStep === 2) {
      if (dupWarning) {
        const em = dupWarning.existing_member || {};
        toast.error(
          `⚠ ${em.member_number || "—"} — ${em.name || "A member"} already has this ${dupWarning.matched_field} registered. Duplicate not allowed.`,
          { duration: 8000 }
        );
        return;
      }
      try {
        setLoading(true);
        // Translate additional fields to Tamil if needed
        const additionalFields = {
          occupation: formData.occupation,
          referrer_name: formData.referrer_name
        };
        let translatedExtras = additionalFields;
        try {
          const tx = await axios.post(`${API}/translate-to-tamil`, additionalFields);
          translatedExtras = tx.data;
        } catch (e) {
          console.warn("Translation skipped");
        }

        const payload = {
          ...formData,
          ...translatedExtras,
          photo_path: photoPath,
          aadhar_path: aadharPath,
          aadhar_back_path: aadharBackPath
        };
        const response = await axios.post(`${API}/create-member`, payload);
        setMemberId(response.data.id);
        setFormData((f) => ({ ...f, member_number: response.data.member_number || f.member_number }));
        // Set form preview URL
        setFormPreviewUrl(`${API}/preview-form/${response.data.id}?t=${Date.now()}`);
        toast.success("Form created successfully!");
        setCurrentStep(3);
      } catch (error) {
        if (process.env.NODE_ENV !== "production") console.error(error);
        const detail = error?.response?.data?.detail;
        if (error?.response?.status === 409 && detail?.error === "duplicate_member") {
          const em = detail.existing_member || {};
          toast.error(
            `Duplicate ${detail.matched_field}: ${em.name || ""} (${em.member_number || "—"}) already exists`,
            { duration: 8000 }
          );
        } else {
          toast.error(typeof detail === "string" ? detail : "Failed to create form.");
        }
      } finally {
        setLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/download-pdf/${memberId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `membership_form_${memberId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF downloaded!");
    } catch (error) {
      if (process.env.NODE_ENV !== "production") console.error(error);
      toast.error("Failed to download PDF");
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const doc = printWindow.document;
    doc.title = "Membership Form";
    const style = doc.createElement('style');
    style.textContent = "body{margin:0;display:flex;justify-content:center;align-items:center}img{max-width:100%;height:auto}@media print{body{margin:0}img{max-width:100%}}";
    doc.head.appendChild(style);
    const img = doc.createElement('img');
    img.onload = () => { printWindow.print(); printWindow.close(); };
    img.src = formPreviewUrl;
    doc.body.appendChild(img);
  };

  const resetForm = () => {
    setCurrentStep(1);
    setAadharData(null);
    setAadharPath("");
    setAadharBackPath("");
    setFrontFile(null);
    setBackFile(null);
    setFrontPreview("");
    setBackPreview("");
    setAadharMode("two_side");
    setPhotoPath("");
    setPhotoPreview("");
    setMemberId("");
    setFormPreviewUrl("");
    setFormData({
      name: "", father_name: "", dob: "", aadhar_number: "", age: "", address: "",
      mobile: "", email: "", occupation: "", referrer_name: "", referrer_number: "",
      member_number: ""
    });
    // Re-fetch next available SH number for the fresh form
    axios.get(`${API}/next-member-number`).then((r) => {
      setFormData((f) => ({ ...f, member_number: r.data.member_number || "" }));
    }).catch(() => {});
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <Toaster position="top-center" richColors />
      
      {/* Top module navigation: Membership · Kitchen · Bar */}
      <div className="bg-slate-950 border-b border-amber-700/40 px-4 py-2" data-testid="top-module-nav">
        <div className="max-w-5xl mx-auto flex items-center gap-2 overflow-x-auto no-scrollbar">
          {!isBarStaff && (
            <button
              onClick={() => setView("create")}
              data-testid="nav-membership-button"
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                view === "create" || view === "dashboard"
                  ? "bg-amber-500 text-slate-950"
                  : "text-amber-300 hover:bg-amber-900/30"
              }`}
            >
              <Users className="w-4 h-4" /> Membership
            </button>
          )}
          {(isAdmin || isBarStaff) && (
            <button
              onClick={() => setView("kitchen")}
              data-testid="nav-kitchen-button"
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                view === "kitchen" ? "bg-amber-500 text-slate-950" : "text-amber-300 hover:bg-amber-900/30"
              }`}
            >
              <ChefHat className="w-4 h-4" /> Kitchen
            </button>
          )}
          {(isAdmin || isBarStaff) && (
            <button
              onClick={() => setView("bar")}
              data-testid="nav-bar-button"
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                view === "bar" ? "bg-amber-500 text-slate-950" : "text-amber-300 hover:bg-amber-900/30"
              }`}
            >
              <Wine className="w-4 h-4" /> Bar
            </button>
          )}
        </div>
      </div>

      {view === "dashboard" ? (
        <Dashboard onBack={() => setView("create")} onGoBar={isAdmin ? () => setView("bar") : null} />
      ) : view === "bar" ? (
        <Bar onBack={() => setView(isBarStaff ? "bar" : "dashboard")} onGoKitchen={null} />
      ) : view === "kitchen" ? (
        <Kitchen onBack={() => setView(isBarStaff ? "bar" : "create")} />
      ) : (
        <>
      <div className="bg-slate-900 border-b border-amber-700/30 px-6 py-5">
        <div className="max-w-md mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="min-w-0">
              <h1 className="text-lg font-bold tracking-[0.18em] text-amber-400 truncate" data-testid="app-header-title">{branding.club_name}</h1>
              <p className="text-xs text-amber-200/70 truncate">{branding.club_subtitle_ta}</p>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {isAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setView("bar")}
                className="rounded-xl text-amber-300 hover:text-amber-400 hover:bg-amber-900/30"
                data-testid="view-bar-button"
                title="Bar"
              >
                <Wine className="w-5 h-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setView("dashboard")}
              className="rounded-xl text-amber-300 hover:text-amber-400 hover:bg-amber-900/30"
              data-testid="view-members-button"
              title="Members"
            >
              <Users className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="rounded-xl text-amber-300 hover:text-red-400 hover:bg-red-950/30"
              data-testid="logout-button"
              title="Logout"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3].map((step) => (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                    currentStep >= step ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-400"
                  }`}
                  data-testid={`step-indicator-${step}`}
                >
                  {currentStep > step ? <CheckCircle2 className="w-5 h-5" /> : step}
                </div>
                <span className="text-xs text-slate-500 mt-2 uppercase tracking-wider font-bold">
                  {step === 1 ? "Aadhar" : step === 2 ? "Details" : "Preview"}
                </span>
              </div>
              {step < 3 && (
                <div className={`flex-1 h-1 mx-2 rounded-full transition-all ${currentStep > step ? "bg-amber-600" : "bg-slate-100"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <div className="max-w-md mx-auto px-6">
        <AnimatePresence mode="wait">
          {currentStep === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <FileText className="w-6 h-6 text-amber-600" />
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Upload Aadhar Card</h2>
                </div>

                {/* Mode selector — Front+Back vs Full Page */}
                <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-xl mb-5" data-testid="aadhar-mode-selector">
                  <button
                    type="button"
                    onClick={() => { setAadharMode("two_side"); clearAadharSelection(); }}
                    className={`h-10 rounded-lg text-sm font-semibold transition-all ${
                      aadharMode === "two_side"
                        ? "bg-white text-amber-700 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="aadhar-mode-two-side"
                  >
                    Front + Back
                  </button>
                  <button
                    type="button"
                    onClick={() => { setAadharMode("full_page"); clearAadharSelection(); }}
                    className={`h-10 rounded-lg text-sm font-semibold transition-all ${
                      aadharMode === "full_page"
                        ? "bg-white text-amber-700 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="aadhar-mode-full-page"
                  >
                    Full Page Aadhar
                  </button>
                </div>

                {aadharMode === "two_side" ? (
                  <div className="space-y-4">
                    {/* FRONT slot */}
                    <SideUploadSlot
                      side="front"
                      label="Front Side"
                      hint="Photo, Aadhar number, DOB"
                      preview={frontPreview}
                      onFile={(f) => selectSide("front", f)}
                      onClear={() => { setFrontFile(null); setFrontPreview(""); }}
                      disabled={loading}
                    />
                    {/* BACK slot */}
                    <SideUploadSlot
                      side="back"
                      label="Back Side (with QR)"
                      hint="Address & QR code"
                      preview={backPreview}
                      onFile={(f) => selectSide("back", f)}
                      onClear={() => { setBackFile(null); setBackPreview(""); }}
                      disabled={loading}
                    />
                    <Button
                      onClick={() => processAadhar([frontFile, backFile])}
                      disabled={loading || (!frontFile && !backFile)}
                      className="w-full h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                      data-testid="process-aadhar-button"
                    >
                      {loading ? "Scanning..." : (frontFile && backFile ? "Scan Front + Back" : "Scan Aadhar")}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">Both sides recommended — QR on back auto-fills all details</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* FULL PAGE slot */}
                    <SideUploadSlot
                      side="full"
                      label="Full Page Aadhar"
                      hint="Print letter with photo + QR + address"
                      preview={frontPreview}
                      onFile={(f) => selectSide("front", f)}
                      onClear={() => { setFrontFile(null); setFrontPreview(""); }}
                      disabled={loading}
                    />
                    <Button
                      onClick={() => processAadhar([frontFile])}
                      disabled={loading || !frontFile}
                      className="w-full h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
                      data-testid="process-aadhar-full-button"
                    >
                      {loading ? "Scanning..." : "Scan Full Page"}
                    </Button>
                    <p className="text-xs text-slate-500 text-center">Best for A4 print letters or scans with both sides on one page</p>
                  </div>
                )}

                {aadharData && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="mt-6 space-y-4">
                    <div className="flex items-center gap-2 text-green-600 mb-4">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">Extracted & translated to Tamil</span>
                    </div>
                    
                    <div>
                      <Label htmlFor="name" className="text-sm font-medium text-slate-900">விண்ணப்பதாரர் பெயர் (Name)</Label>
                      <Input id="name" name="name" value={formData.name} onChange={handleInputChange}
                        className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="name-input" />
                    </div>
                    <div>
                      <Label htmlFor="father_name" className="text-sm font-medium text-slate-900">தந்தை / கணவர் பெயர் (Father/Husband)</Label>
                      <Input id="father_name" name="father_name" value={formData.father_name} onChange={handleInputChange}
                        className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="father-name-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="age" className="text-sm font-medium text-slate-900">வயது (Age)</Label>
                        <Input id="age" name="age" value={formData.age} onChange={handleInputChange}
                          className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="age-input" />
                      </div>
                      <div>
                        <Label htmlFor="dob" className="text-sm font-medium text-slate-900">பிறந்த தேதி (DOB)</Label>
                        <Input id="dob" name="dob" value={formData.dob} onChange={handleInputChange}
                          className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="dob-input" />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="address" className="text-sm font-medium text-slate-900">முகவரி (Address)</Label>
                      <Textarea id="address" name="address" value={formData.address} onChange={handleInputChange}
                        className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white min-h-[80px]" data-testid="address-input" />
                    </div>
                    <div>
                      <Label htmlFor="aadhar_number" className="text-sm font-medium text-slate-900">ஆதார் எண் (Aadhar)</Label>
                      <Input id="aadhar_number" name="aadhar_number" value={formData.aadhar_number} onChange={handleInputChange}
                        className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="aadhar-number-input" />
                      {dupWarning && (
                        <div className="mt-2 flex items-start gap-2 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-700" data-testid="duplicate-warning-banner">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <div>
                            <b>{dupWarning.existing_member?.member_number || "—"}</b> — {dupWarning.existing_member?.name || "A member"} already has this {dupWarning.matched_field === "Aadhar" ? "Aadhar" : "mobile number"} registered. Duplicate members cannot be created.
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </div>
            </motion.div>
          )}

          {currentStep === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              {/* Photo upload */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-6">
                <div className="flex items-center gap-3 mb-6">
                  <User className="w-6 h-6 text-amber-600" />
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Passport Photo</h2>
                </div>
                
                {!photoPreview ? (
                  <div
                    {...getPhotoRootProps()}
                    className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      isPhotoDragActive ? "border-amber-400 bg-amber-50" : "border-amber-200 bg-amber-50/50 hover:border-amber-400 hover:bg-amber-50"
                    }`}
                    data-testid="upload-photo-dropzone"
                  >
                    <input {...getPhotoInputProps()} data-testid="upload-photo-input" />
                    <Camera className="w-12 h-12 text-amber-600 mx-auto mb-4" />
                    <p className="text-base font-medium text-slate-900 mb-2">
                      {loading ? "Uploading..." : "Drop photo here"}
                    </p>
                    <p className="text-sm text-slate-500">JPG, PNG, PDF, HEIC supported</p>
                  </div>
                ) : (
                  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
                    <div className="flex items-center gap-2 text-green-600 mb-4 justify-center">
                      <CheckCircle2 className="w-5 h-5" />
                      <span className="text-sm font-medium">Photo uploaded</span>
                    </div>
                    <img src={photoPreview} alt="Member" className="w-40 h-48 rounded-xl object-cover mx-auto shadow-md border-2 border-slate-100" data-testid="photo-preview" />
                    <Button variant="outline" onClick={() => { setPhotoPath(""); setPhotoPreview(""); }}
                      className="mt-4 rounded-xl" data-testid="change-photo-button">
                      Change Photo
                    </Button>
                  </motion.div>
                )}

                {/* Camera capture button for selfie photo */}
                {!photoPreview && (
                  <div className="mt-3">
                    <input
                      type="file"
                      accept="image/*"
                      capture="user"
                      className="hidden"
                      id="photo-camera-input"
                      onChange={(e) => {
                        if (e.target.files?.[0]) uploadPhotoFile(e.target.files[0]);
                        e.target.value = "";
                      }}
                      data-testid="photo-camera-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById("photo-camera-input").click()}
                      className="w-full h-11 rounded-xl border-amber-300 text-amber-700 hover:bg-amber-50 gap-2 font-medium"
                      data-testid="photo-camera-button"
                    >
                      <Camera className="w-4 h-4" />
                      Take Selfie / Photo
                    </Button>
                  </div>
                )}
              </div>

              {/* Additional fields */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8 mb-6">
                <h3 className="text-xl font-medium tracking-tight text-slate-900 mb-5">Additional Details</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="mobile" className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <Phone className="w-4 h-4" /> கைபேசி எண் (Mobile)
                    </Label>
                    <Input id="mobile" name="mobile" value={formData.mobile} onChange={handleInputChange}
                      placeholder="9876543210" className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="mobile-input" />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <Mail className="w-4 h-4" /> மின்னஞ்சல் (Email)
                    </Label>
                    <Input id="email" name="email" value={formData.email} onChange={handleInputChange}
                      placeholder="example@email.com" className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="email-input" />
                  </div>
                  <div>
                    <Label htmlFor="member_number" className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <FileText className="w-4 h-4" /> உறுப்பினர் எண் (Member ID)
                    </Label>
                    <Input id="member_number" name="member_number" value={formData.member_number} readOnly disabled
                      placeholder="Auto-generated..." className="mt-1.5 rounded-xl bg-slate-100 text-slate-900 font-semibold tracking-wider cursor-not-allowed" data-testid="member-number-input" />
                    <p className="text-xs text-slate-400 mt-1">Auto-generated as SH &lt;highest + 1&gt;. Editable later from the Dashboard.</p>
                  </div>
                  <div>
                    <Label htmlFor="occupation" className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <Briefcase className="w-4 h-4" /> தொழில் / பணியிடம் (Occupation)
                    </Label>
                    <Input id="occupation" name="occupation" value={formData.occupation} onChange={handleInputChange}
                      placeholder="Business / Job" className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="occupation-input" />
                  </div>
                  <div>
                    <Label htmlFor="referrer_name" className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                      <Users className="w-4 h-4" /> பரிந்துரையாளர் பெயர் (Referrer Name)
                    </Label>
                    <Input id="referrer_name" name="referrer_name" value={formData.referrer_name} onChange={handleInputChange}
                      placeholder="Referrer's full name" className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="referrer-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="referrer_number" className="text-sm font-medium text-slate-900">பரிந்துரையாளர் எண் (Referrer Number)</Label>
                    <Input id="referrer_number" name="referrer_number" value={formData.referrer_number} onChange={handleInputChange}
                      placeholder="Member ID" className="mt-1.5 rounded-xl bg-slate-50 focus:bg-white" data-testid="referrer-number-input" />
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {currentStep === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
              <div className="bg-white rounded-3xl shadow-md border border-slate-100 p-6 mb-32" data-testid="membership-preview">
                <h2 className="text-xl font-semibold tracking-tight text-slate-900 text-center mb-4">Membership Form</h2>
                
                {formPreviewUrl ? (
                  <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                    <img src={formPreviewUrl} alt="Filled membership form" className="w-full h-auto" data-testid="form-preview-image" />
                  </div>
                ) : (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
                    Loading preview...
                  </div>
                )}

                <div className="flex flex-col gap-3 mt-6">
                  <Button onClick={handleDownloadPDF}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 rounded-xl font-medium"
                    data-testid="download-pdf-button">
                    <Download className="w-5 h-5 mr-2" />
                    Download PDF
                  </Button>
                  <Button onClick={handlePrint} variant="outline"
                    className="w-full h-12 rounded-xl font-medium border-amber-200 text-amber-600 hover:bg-amber-50"
                    data-testid="print-button">
                    <Printer className="w-5 h-5 mr-2" />
                    Print
                  </Button>
                </div>

                <div className="flex flex-col gap-2 mt-4 pt-4 border-t border-slate-100">
                  <Button onClick={resetForm} variant="ghost"
                    className="w-full text-slate-600 hover:text-slate-900 rounded-xl"
                    data-testid="create-new-button">
                    Create New Member
                  </Button>
                  <Button onClick={() => setView("dashboard")} variant="ghost"
                    className="w-full text-amber-600 hover:text-amber-700 rounded-xl gap-2"
                    data-testid="view-all-members-button">
                    <Users className="w-4 h-4" /> View All Members
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {currentStep < 3 && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-[100]">
          <div className="max-w-md mx-auto flex gap-3">
            {currentStep > 1 && (
              <Button onClick={() => setCurrentStep(currentStep - 1)} variant="outline"
                className="flex-1 h-12 rounded-xl font-medium" data-testid="back-button">
                Back
              </Button>
            )}
            <Button onClick={handleNextStep}
              disabled={loading || (currentStep === 1 && !aadharData) || (currentStep === 2 && !photoPath)}
              className="flex-1 bg-amber-600 hover:bg-amber-700 text-white h-12 rounded-xl font-medium disabled:opacity-50"
              data-testid="next-step-button">
              {loading ? "Processing..." : currentStep === 2 ? "Generate Form" : "Next"}
            </Button>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

function ProtectedApp() {
  const { user, loading } = useAuth();

  if (loading || user === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-500 text-sm">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return <MainApp />;
}

function App() {
  return (
    <BrandingProvider>
      <AuthProvider>
        <ProtectedApp />
      </AuthProvider>
    </BrandingProvider>
  );
}

export default App;
